import { types } from "../actions";
import { SEARCH_STATUS_PAYLOAD_UPLOADING } from "../../pages/HistoricalSearch/SearchConstants";

// In-flight: no imageId yet and status undefined/uploading. Anything else (uploaded/failed/etc.)
// is terminal - once resolved, an entry's outcome is known and must not be reverted.
const isEntryResolved = (entry) =>
  Boolean(entry?.imageId) || (entry?.status !== undefined && entry?.status !== SEARCH_STATUS_PAYLOAD_UPLOADING);

// A REST "get search by id" refetch (e.g. on page revisit) can race a backend-side imageId
// persistence step (TS Core) that's independent of the actual S3 upload - if that persistence
// lags or fails, REST returns an entry with no imageId/status even though WS already confirmed
// it uploaded. Blindly replacing the record (as FETCHED_FORENSIC_SEARCH_RESULTS used to) would
// wipe that WS-confirmed state and cause SearchResult.jsx to re-trigger the index API for an
// image that's already uploaded. Merge entry-by-entry (keyed by fovId:imageIndex) instead,
// keeping whichever side is already resolved when the other side regresses.
const mergeSearchEntries = (existingResults = [], incomingResults = []) => {
  const existingByKey = new Map(existingResults.map((entry) => [`${entry.fovId}:${entry.imageIndex}`, entry]));
  return incomingResults.map((entry) => {
    const existing = existingByKey.get(`${entry.fovId}:${entry.imageIndex}`);
    if (existing && isEntryResolved(existing) && !isEntryResolved(entry)) {
      return existing;
    }
    return entry;
  });
};

const initialState = {
   forensicSearchResultList: {statusCode: null,statusText: "", responseData: []},
   deleteSearchResultResponse: {},
   imageIndexFetchResult: {}
};

const forensicSearchReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.FETCHED_FORENSIC_SEARCH_RESULTS: {
        const searchResult = action?.searchResult || {statusCode: null,statusText: "", responseData: []};
        // Single-search fetches (resourceidentifier) must upsert into the existing list
        // instead of replacing it, otherwise polling one search wipes the full recent-searches list.
        if (searchResult.isSingleFetch) {
            const existingList = state.forensicSearchResultList.responseData || [];
            const results = searchResult.responseData || [];
            // TODO: remove this id-matching once backend guarantees a single record for a
            // resourceidentifier fetch - fall back to the first item only if none match the requested id.
            const singleResult = results.find((r) => r.id === searchResult.requestedId) || results[0];
            const existingMatch = singleResult && existingList.find((item) => item.id === singleResult.id);
            const mergedResult = existingMatch
              ? { ...singleResult, results: mergeSearchEntries(existingMatch.results, singleResult.results) }
              : singleResult;
            const responseData = !singleResult
              ? existingList
              : existingMatch
                ? existingList.map((item) => (item.id === singleResult.id ? mergedResult : item))
                : [...existingList, singleResult];
            return {
                ...state,
                forensicSearchResultList: { ...searchResult, responseData },
                deleteSearchResultResponse: {}
            }
        }
        return {
            ...state,
            forensicSearchResultList: searchResult,
            deleteSearchResultResponse: {}
        }
    }
    case types.CREATE_FORENSIC_SEARCH_RESPONSE: {
        const existing = state.forensicSearchResultList.responseData;
        const newSearch = action.payload.responseData;
        if (!newSearch?.id) {
            return {
                ...state,
                forensicSearchResultList: {
                    statusCode: action.payload.statusCode,
                    statusText: action.payload?.statusText ?? "",
                    responseData: existing,
                }
            };
        }
        const existsInList = existing.some(m => m.id === newSearch.id);
        // Prepend when new (keeps desc createdon order); replace in-place when already present (retry/race).
        const updatedResponseData = existsInList
            ? existing.map(m => m.id === newSearch.id ? newSearch : m)
            : [newSearch, ...existing];
        return {
            ...state,
            forensicSearchResultList: {
                statusCode: action.payload.statusCode,
                statusText: action.payload?.statusText ?? "",
                responseData: updatedResponseData,
            },
            deleteSearchResultResponse: {}
        }
    }
     case types.DELETE_FORENSIC_SEARCH_RESPONSE: {
        const deletedId = action.payload.statusCode === 200 ? action.payload.responseData?.id : null;
        return {
            ...state,
            forensicSearchResultList: deletedId
              ? {
                  ...state.forensicSearchResultList,
                  responseData: state.forensicSearchResultList.responseData.filter((item) => item.id !== deletedId),
                }
              : state.forensicSearchResultList,
            deleteSearchResultResponse: {"statusCode": action.payload.statusCode,"statusText": action.payload?.statusText ?? "", "responseData" : action.payload.responseData},

        }
    }
     case types.GET_FORENSIC_SEARCH_STATUS_RESPONSE: {
        return {
            ...state,
            forensicSearchResultList: {
              ...state.forensicSearchResultList,
              // Preserve isSingleFetch/requestedId - SearchResult.jsx's status effect gates on
              // these to distinguish "update for the search I'm viewing" from "list page's own
              // fetch landed in this shared slice" (see SearchResult.jsx's forensicSearchResultList
              // effect). Without them, a WS-driven status merge (e.g. search-completed Kafka event)
              // gets treated as list-page noise and silently ignored until the next REST poll.
              "statusCode": action.payload.statusCode,
              "statusText": action.payload?.statusText ?? "",
              "isSingleFetch": true,
              "requestedId": action.payload.responseData.id,
              "responseData": state.forensicSearchResultList.responseData.map(m => {
              if(m.id === action.payload.responseData.id){
                return {...m, ...action.payload.responseData}
              }
              return m;
            })}
        }
    }
    case types.UPDATE_FORENSIC_SEARCH_IMAGES_RESPONSE: {
        const { searchId, images } = action.payload;
        // Match by entryId, not imageId - a pending entry has no imageId yet (that's the field
        // this event assigns for the first time). entryId mirrors entry.imageIndex, the value
        // originally sent as searchIndex.entryids when the upload was triggered.
        const imagesByEntryId = new Map((images || []).map((img) => [img.entryId, img]));
        return {
            ...state,
            forensicSearchResultList: {
                ...state.forensicSearchResultList,
                responseData: state.forensicSearchResultList.responseData.map((search) => {
                    if (search.id !== searchId) return search;
                    return {
                        ...search,
                        results: (search.results || []).map((entry) => {
                            const update = imagesByEntryId.get(entry.imageIndex);
                            return update ? { ...entry, ...update } : entry;
                        })
                    };
                })
            }
        };
    }
    case types.GET_IMAGE_URL_FORENSIC_SEARCH_IMAGE_INDEX_RESPONSE: {
        // Per-entry image fetch response must not drive the search's overall status - with
        // multiple entries on a page, each response lands independently and whichever arrives
        // last would win, racing against/short-circuiting the real status polling
        // (getSearchStatusAPI). Overall done/uploading state comes only from that poll.
        // imageIndexFetchResult is a separate slice so SearchResult.jsx can still see failures.
        // Keyed by fovid:entryId so each per-entry dispatch gets its own slot - takeEvery runs
        // one saga per dispatch and concurrent entries must not clobber each other.
        const fovid = action.payload?.responseData?.searchIndex?.fovid;
        const entryId = action.payload?.responseData?.searchIndex?.entryids?.[0];
        const entryKey = fovid != null && entryId != null ? `${fovid}:${entryId}` : null;
        return {
            ...state,
            imageIndexFetchResult: entryKey
              ? { ...state.imageIndexFetchResult, [entryKey]: action.payload }
              : state.imageIndexFetchResult
        };
    }
    
    default:
      return state;
  }
};
export default forensicSearchReducer;